import { AUDIENCES, DOC_TYPES, docTypeOf } from "@klarity/schema";
import type { TreeDoc } from "../../lib/api/models";

/** A document in a group. `depth` is how many documents it lies below inside the group; none means zero. */
export type GroupDoc = TreeDoc & { depth?: number };
/** `docId` is the page the group is named after, when there is one: the heading links to it. */
export type DocGroup = { key: string; heading: string; docId?: string; docs: GroupDoc[] };

const inTreeOrder = (a: TreeDoc, b: TreeDoc) => a.position - b.position || a.title.localeCompare(b.title);

/**
 * The top-level documents of a topic by type, in the order of the type list (document types spec §4.2). Documents
 * without a known type follow as "Other", wiki pages come last. Inside a group the order is the tree's.
 */
export function groupByType(docs: TreeDoc[]): DocGroup[] {
  const top = docs.filter((d) => d.parentId === null).sort(inTreeOrder);
  const keyOf = (d: TreeDoc) => (d.kind === "page" ? "pages" : (docTypeOf(d.code)?.key ?? "other"));
  const order: Array<{ key: string; heading: string }> = [
    ...DOC_TYPES,
    { key: "other", heading: "Other" },
    { key: "pages", heading: "Pages" },
  ];
  return order
    .map(({ key, heading }) => ({ key, heading, docs: top.filter((d) => keyOf(d) === key) }))
    .filter((g) => g.docs.length > 0);
}

/**
 * The top-level templates by who they are written for, in the order of the audience list: Klarity's own people,
 * then clients. Inside a group the order is the tree's.
 */
export function groupByAudience(docs: TreeDoc[]): DocGroup[] {
  const top = docs.filter((d) => d.parentId === null).sort(inTreeOrder);
  return AUDIENCES.map(({ key, heading }) => ({ key, heading, docs: top.filter((d) => d.audience === key) })).filter(
    (g) => g.docs.length > 0,
  );
}

/**
 * A client's documents under its top-level pages, in the tree's order (a new client gets "Network and
 * infrastructure", "Runbooks", "Proposals and contracts" and "Handover"). A group lists everything below its
 * page, depth first, and stays when it is empty. Top-level documents that are not pages follow as "Other
 * documents", each with what lies below it.
 */
export function groupByFolder(docs: TreeDoc[]): DocGroup[] {
  const children = new Map<string | null, TreeDoc[]>();
  for (const d of docs) children.set(d.parentId, [...(children.get(d.parentId) ?? []), d]);
  const below = (id: string, depth: number): GroupDoc[] =>
    [...(children.get(id) ?? [])].sort(inTreeOrder).flatMap((d) => [{ ...d, depth }, ...below(d.id, depth + 1)]);
  const groups: DocGroup[] = [];
  const other: GroupDoc[] = [];
  for (const top of [...(children.get(null) ?? [])].sort(inTreeOrder)) {
    if (top.kind === "page") groups.push({ key: top.id, heading: top.title, docId: top.id, docs: below(top.id, 0) });
    else other.push({ ...top, depth: 0 }, ...below(top.id, 1));
  }
  if (other.length > 0) groups.push({ key: "other", heading: "Other documents", docs: other });
  return groups;
}
